import { Order } from "@/interfaces";
import { roundCurrency } from "@/utils";

export class OrderHelper {
  // Amount remaining after EVERY reduction, gift card included. This is the
  // legacy "total" calc — it's why an $11 order paid with a $25 gift card
  // rendered as $0 (the gift card drove it negative, then it got clamped).
  // Kept only so nothing that still relies on the old semantics breaks; new
  // UI should use getOrderTotal + the gift-card helpers below instead.
  static getTotal = (order?: Order) =>
    roundCurrency(
      (order?.total || 0) -
        (order?.coopUsed || 0) -
        (order?.couponUsed || 0) -
        (order?.discount || 0) +
        (order?.shippingFee || 0)
    );

  // The actual cost of the order: subtotal − co-op − discounts + shipping.
  // Deliberately EXCLUDES the gift card, because a gift card is a payment
  // method, not a price reduction. A $25 gift card against an $11 order still
  // leaves an $11 order total. Clamped at 0 (co-op + discounts can exceed the
  // subtotal on fully-comped orders).
  static getOrderTotal = (order?: Order) =>
    roundCurrency(
      Math.max(
        0,
        (order?.total || 0) -
          (order?.coopUsed || 0) -
          (order?.discount || 0) +
          (order?.shippingFee || 0)
      )
    );

  // How much of the presented gift card was actually consumed by this order —
  // never more than the order total (you can't spend $25 of a gift card on an
  // $11 order).
  static getGiftCardApplied = (order?: Order) =>
    roundCurrency(
      Math.min(order?.couponUsed || 0, OrderHelper.getOrderTotal(order))
    );

  // Face value of the gift card left unspent after covering this order.
  static getGiftCardLeftover = (order?: Order) =>
    roundCurrency(
      Math.max(0, (order?.couponUsed || 0) - OrderHelper.getOrderTotal(order))
    );

  // What still had to be paid (typically by credit card) once the gift card
  // was applied.
  static getBalanceDue = (order?: Order) =>
    roundCurrency(
      Math.max(0, OrderHelper.getOrderTotal(order) - (order?.couponUsed || 0))
    );
}
